import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useAppSelector } from '../../../store'
import { IntTplState, setScreenHeader } from '../../../store/internationalTplSlice'
import { AuthState } from '../../../store/authSlice'
import { AppState } from '../../../store/appSlice'
import { getInternationalTplPages } from './pagesConfig'
import { useTranslation } from 'react-i18next'
import { InternationalTplPageSlug } from '../../../domain/types'
import { PageInfo } from '../../../components/PageHeader'

const LOGIN_PAGE_ROUTE = '/international-tpl-user'
const PAYMENT_PAGE_ROUTE = '/international-tpl-payment'

// Valid API page slugs (excludes payment page)
const VALID_API_SLUGS = new Set<InternationalTplPageSlug>([
  'international-tpl-which-country-to-drive',
  'international-tpl-insurance-period',
  'international-tpl-personal-info',
  'international-tpl-car-info',
  'international-tpl-offer',
  'international-tpl-check-info',
])

export const useInternationalTplProgress = () => {
  const location = useLocation()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { screenHeader, pageData } = useAppSelector((data: { internationalTpl: IntTplState }) => data.internationalTpl)
  const auth = useAppSelector((data: { auth: AuthState }) => data.auth)
  const { lang } = useAppSelector((root: { app: AppState }) => root.app)

  // Get all pages from config
  const configPages = getInternationalTplPages(t)

  // Check if user is logged in
  const isLoggedIn = !!(auth.token && auth.user)

  // Filter out payment page and only keep pages with valid API slugs
  // Use stepName from API if available, otherwise fall back to config title
  const apiPagesOnly = useMemo(() => {
    return configPages
      .filter((page) => page.route !== PAYMENT_PAGE_ROUTE && VALID_API_SLUGS.has(page.slug))
      .map((page): PageInfo => {
        const apiPageData = pageData[page.slug]
        // Use stepName from API if available, otherwise fall back to config title
        const title = apiPageData?.translations?.[lang]?.stepName || page.title
        return {
          route: page.route,
          title,
        }
      })
  }, [configPages, pageData, lang])

  // Create pages array with login page included (after offer, before check-info)
  const allPagesWithLogin = useMemo(() => {
    const offerIndex = apiPagesOnly.findIndex((p) => p.route === '/international-tpl-offer')
    if (offerIndex >= 0) {
      const pagesWithLogin: PageInfo[] = [...apiPagesOnly]
      pagesWithLogin.splice(offerIndex + 1, 0, {
        route: LOGIN_PAGE_ROUTE,
        title: t('login') || 'Login',
      })
      return pagesWithLogin
    }
    return apiPagesOnly
  }, [apiPagesOnly, t])

  // Filter out login page if user is logged in
  const visiblePages = useMemo(() => {
    if (isLoggedIn) {
      return allPagesWithLogin.filter((page) => page.route !== LOGIN_PAGE_ROUTE)
    }
    return allPagesWithLogin
  }, [allPagesWithLogin, isLoggedIn])

  // Calculate total count: API pages (6) + login page (1) = 7, minus 1 if logged in = 6
  // Total should always be 7 when not logged in, 6 when logged in
  const totalCount = isLoggedIn ? 6 : 7

  // Find current page index in the visible pages array
  const currentPageIndex = visiblePages.findIndex((page) => page.route === location.pathname)

  // Calculate passed count
  let passedCount = currentPageIndex >= 0 ? currentPageIndex + 1 : screenHeader.passedCount

  // Update Redux when route changes or auth status changes
  useEffect(() => {
    if (totalCount !== screenHeader.totalCount || passedCount !== screenHeader.passedCount) {
      dispatch(
        setScreenHeader({
          totalCount,
          passedCount,
        }),
      )
    }
  }, [totalCount, passedCount, screenHeader.totalCount, screenHeader.passedCount, dispatch])

  return {
    totalCount,
    passedCount,
    currentPageIndex,
    isLoggedIn,
    pages: visiblePages,
  }
}
